import classNames from "classnames";
import { useEffect, useState, useRef } from "react";
import SendLogo from "../../assets/icons/send";
import Microphone from "../../assets/icons/microphone";
import UploadIcon from "../../assets/icons/upload";
import VoiceModeIcon from "../../assets/icons/voiceMode";
import UploadingLoader from "../../lib/common/components/loader/UploadingLoader";
import { Download, SquareTerminal, X, Check } from "lucide-react";
import SavePromptModal from "../prompts/SavePromptModal";
import PromptLibraryDialog from "../prompts/PromptLibraryDialog";
import { savePrompt } from "../../lib/redux/actions";
import toast from "../../lib/utils/toast";
import { Button, Select, MenuItem, FormControl } from "@mui/material";
import PropTypes from "prop-types";

export default function TextInput({
  placeholder,
  inputFocused,
  inputRef,
  setInputFocused,
  setInputText,
  inputText,
  handleChatResponse,
  loading,
  fileInputRef,
  handleFileChange,
  isUploading,
  files,
  setFiles,
  chats,
  handleOpen,
  isFileUploadOptionVisible = true,
  isVoiceModeOn = false,
  allowedFileTypes = ".csv,.xlsx,.pdf,.docx,.jpg,.jpeg,.png",
  isContentRendering = false,
  hasDocument,
  isMultipleFileUploadEnabled = false,
  requireMediaToSend = false,
  showPromptActions = true,
  showInlineUtilities = true,
  useNativePlaceholder = false,
  inputVariant = "boxed",
  showModelSelector = false,
  modelOptions = {},
  modelName = "",
  onModelChange = () => {},
  isVideo = false,
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const recognitionRef = useRef(null);
  const preRecordingTextRef = useRef("");
  const isRecordingRef = useRef(false);
  const mediaStreamRef = useRef(null);
  const analyserRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const amplitudeHistoryRef = useRef([]);

  const [openSavePromptDialog, setOpenSavePromptDialog] = useState(false);
  const [openPromptLibraryDialog, setOpenPromptLibraryDialog] = useState(false);

  useEffect(() => {
    initializeRecognition();
    return () => {
      recognitionRef.current?.removeEventListener(
        "end",
        recognitionRef.current?.onend
      );
      stopWaveform();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isRecording) {
      startWaveform();
    }
    return () => {
      if (!isRecording) stopWaveform();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]);

  const initializeRecognition = () => {
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      console.warn("Speech Recognition not supported in this browser.");
      return;
    }
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.lang = "en-US";
    recognitionRef.current.interimResults = true;
    recognitionRef.current.continuous = true;

    recognitionRef.current.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      const base = preRecordingTextRef.current;
      const separator = base && !base.endsWith(" ") ? " " : "";
      setInputText(base + separator + transcript);
    };

    recognitionRef.current.onend = () => {
      if (isRecordingRef.current) {
        try {
          recognitionRef.current?.start();
        } catch (e) {
          // already running
        }
      }
    };

    recognitionRef.current.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      if (
        event.error === "not-allowed" ||
        event.error === "service-not-allowed"
      ) {
        isRecordingRef.current = false;
        setIsRecording(false);
        stopWaveform();
      }
    };
  };

  const startRecording = () => {
    if (!recognitionRef.current) {
      console.error("Recognition is not initialized.");
      return;
    }
    if (isRecording) return;
    preRecordingTextRef.current = inputText;
    isRecordingRef.current = true;
    setIsRecording(true);
    recognitionRef.current.start();
  };

  const confirmRecording = () => {
    isRecordingRef.current = false;
    recognitionRef.current?.stop();
    setIsRecording(false);
  };

  const cancelRecording = () => {
    isRecordingRef.current = false;
    recognitionRef.current?.stop();
    setIsRecording(false);
    setInputText(preRecordingTextRef.current);
  };

  const startWaveform = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      await new Promise((resolve) => setTimeout(resolve, 50));
      if (!canvasRef.current) return;

      const rect = canvasRef.current.parentElement.getBoundingClientRect();
      canvasRef.current.width = rect.width * (window.devicePixelRatio || 1);
      canvasRef.current.height = rect.height * (window.devicePixelRatio || 1);

      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      amplitudeHistoryRef.current = [];
      drawWaveform();
    } catch (err) {
      console.error("Microphone access error:", err);
    }
  };

  const stopWaveform = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    amplitudeHistoryRef.current = [];
  };

  const drawWaveform = () => {
    if (!canvasRef.current || !analyserRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.fftSize);
    const dpr = window.devicePixelRatio || 1;
    const barWidth = 3 * dpr;
    const gap = 2 * dpr;
    const maxBars = Math.floor(canvas.width / (barWidth + gap));

    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      const amplitude = Math.min(1, rms * 4);

      amplitudeHistoryRef.current.push(amplitude);
      if (amplitudeHistoryRef.current.length > maxBars) {
        amplitudeHistoryRef.current.shift();
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const history = amplitudeHistoryRef.current;
      const totalWidth = history.length * (barWidth + gap);
      const startX = (canvas.width - totalWidth) / 2;
      const centerY = canvas.height / 2;

      ctx.fillStyle = "#1B5C29";
      for (let i = 0; i < history.length; i++) {
        const h = Math.max(3 * dpr, history[i] * canvas.height * 0.8);
        const x = startX + i * (barWidth + gap);
        if (ctx.roundRect) {
          ctx.beginPath();
          ctx.roundRect(x, centerY - h / 2, barWidth, h, barWidth / 2);
          ctx.fill();
        } else {
          ctx.fillRect(x, centerY - h / 2, barWidth, h);
        }
      }
    };

    draw();
  };

  const handleSend = async () => {
    if (isRecording) {
      isRecordingRef.current = false;
      recognitionRef.current?.stop();
      setIsRecording(false);
      stopWaveform();
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
    const hasText = inputText.trim()?.length > 0;
    const hasMedia = files?.length > 0;

    if (requireMediaToSend && (!hasMedia || !hasText)) {
      return;
    }

    // Custom validation: Prevent sending images without text (even when requireMediaToSend is false)
    if (hasMedia && !hasText) {
      return;
    }

    if ((requireMediaToSend && hasMedia && hasText) || (!requireMediaToSend && (hasMedia || hasText))) {
      const payload = {
        ...(hasMedia && { media: files }),
        ...(hasText && { text: inputText }),
      };
      handleChatResponse(payload);

     if(!isVideo && !useNativePlaceholder)
      {
        setInputText("");
      }
      files?.length > 0 && setFiles([]);
      if (fileInputRef?.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handlePaste = async (e) => {
    if (!isFileUploadOptionVisible) return;

    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.indexOf("image") !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          // Create a new FileList-like object
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);

          // Create a new event with the pasted file
          const event = {
            target: {
              files: dataTransfer.files,
            },
          };

          // Call the existing handleFileChange with the new event
          handleFileChange(event);
        }
      }
    }
  };

  const handleDragEnter = (e) => {
    if (!isFileUploadOptionVisible) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (!isFileUploadOptionVisible) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    if (!isFileUploadOptionVisible) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    if (!isFileUploadOptionVisible) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles?.length > 0) {
      // Create a new event with the dropped files
      const event = {
        target: {
          files: droppedFiles,
        },
      };
      handleFileChange(event);
    }
  };

  function handleOnPromptSave(title) {
    const payload = {
      title: title,
      description: inputText,
    };

    try {
      savePrompt(payload).then(() => {
        toast.success("Prompt saved successfully!");
        setOpenSavePromptDialog(false);
        setInputText("");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      });
    } catch (error) {
      toast.error("Error saving prompt. Please try again.");
    }
  }

  return (
    <div>
      {openSavePromptDialog && (
        <SavePromptModal
          isOpen={openSavePromptDialog}
          onClose={() => setOpenSavePromptDialog(false)}
          currentPrompt={inputText}
          onPromptSave={handleOnPromptSave}
        />
      )}
      {openPromptLibraryDialog && (
        <PromptLibraryDialog
          isOpen={openPromptLibraryDialog}
          onClose={() => setOpenPromptLibraryDialog(false)}
        />
      )}

      {/* Keep the file input in the DOM even when utilities are rendered elsewhere */}
      {isFileUploadOptionVisible && (
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={allowedFileTypes}
          multiple={isMultipleFileUploadEnabled}
          className="hidden"
          disabled={
            isUploading ||
            chats[chats?.length - 1]?.doc ||
            (isMultipleFileUploadEnabled && files?.length >= 3) ||
            (!isMultipleFileUploadEnabled && files?.length === 1) ||
            isContentRendering ||
            hasDocument
          }
        />
      )}

      {showPromptActions && (
        <div className="flex justify-between mx-4">
          <Button
            size="small"
            onClick={() => setOpenSavePromptDialog(true)}
            startIcon={<Download size={"16px"} />}
            disabled={loading || isContentRendering || inputText?.length === 0}
            sx={{ textTransform: "none" }}
          >
            Save this to My Prompt
          </Button>
          <Button
            size="small"
            onClick={() => setOpenPromptLibraryDialog(true)}
            startIcon={<SquareTerminal size={"16px"} />}
            disabled={loading || isContentRendering}
            sx={{ textTransform: "none" }}
          >
            Prompt Library
          </Button>
        </div>
      )}
      <div
        className={classNames({
          relative: true,
          "border-2 border-dashed border-[#1B5C29] rounded-[14px]":
            isDragging && isFileUploadOptionVisible && inputVariant !== "flat",
        })}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        role="region"
        aria-label="File upload dropzone"
        tabIndex={0}
      >
        <textarea
          ref={inputRef}
          type="text"
          placeholder={
            useNativePlaceholder
              ? placeholder || "Ask DaburGPT"
              : undefined
          }
          className={classNames(
            "text-[14px] !h-[72px] resize-none font-segoeUI !font-normal content-center w-full !py-1 !text-black !placeholder-[#667085] focus:outline-none break-words",
            {
              "!bg-white": inputVariant !== "flat",
              "!bg-transparent": inputVariant === "flat",
              "rounded-[14px] border border-[#DFDFF1] shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)]":
                inputVariant !== "flat",
              "border-0 shadow-none rounded-none": inputVariant === "flat",
              "sm:pr-40 pr-40": showInlineUtilities && !showModelSelector,
              "sm:pr-[250x] pr-[290px]": showInlineUtilities && showModelSelector,
              "sm:pr-5 pr-5": !showInlineUtilities,
              "!pl-5": inputVariant !== "flat",
              "!pl-0": inputVariant === "flat",
            }
          )}
          value={inputText}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e?.preventDefault();
              !loading &&
                !isContentRendering &&
                (!requireMediaToSend || (files?.length > 0 && inputText?.trim()?.length > 0)) &&
                !(files?.length > 0 && !inputText?.trim()?.length) &&
                handleSend();
            }
          }}
          onChange={(e) => setInputText(e?.target?.value)}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
          onPaste={handlePaste}
        />
        {isDragging && isFileUploadOptionVisible && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-[14px] pointer-events-none">
            <span className="text-[#1B5C29] font-medium">Drop image here</span>
          </div>
        )}

        {isRecording && (
          <div
            className={classNames(
              "absolute inset-0 z-10 flex items-center",
              {
                "bg-white rounded-[14px] border border-[#1B5C29] shadow-[0_0_0_3px_rgba(27,92,41,0.12)]":
                  inputVariant !== "flat",
                "bg-white": inputVariant === "flat",
              }
            )}
          >
            <div className="flex-1 h-full px-4 py-3 overflow-hidden">
              <canvas ref={canvasRef} className="w-full h-full" />
            </div>
            <div className="flex items-center gap-2 pr-4">
              <button
                type="button"
                onClick={cancelRecording}
                className="flex items-center justify-center w-9 h-9 rounded-full border border-[#E0E0E0] hover:bg-gray-100 cursor-pointer transition-colors"
                title="Cancel"
              >
                <X size={18} color="#666" />
              </button>
              <button
                type="button"
                onClick={confirmRecording}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-[#1B5C29] hover:bg-[#164a22] cursor-pointer transition-colors"
                title="Done"
              >
                <Check size={18} color="#fff" />
              </button>
            </div>
          </div>
        )}

        {useNativePlaceholder  && !inputText && !inputFocused  && (
          <div
            className={classNames(
              "absolute font-inter text-[#1B5C2980] top-1/2 left-5 transform -translate-y-1/2 pointer-events-none text-[14px] leading-[20px] font-normal",
              {
                "!right-28": showInlineUtilities && !showModelSelector,
                "!right-[180px] sm:!right-[200px]": showInlineUtilities && showModelSelector,
                "!right-5": !showInlineUtilities,
              }
            )}
            // style={{ right: "112px" }}
          >
            {!useNativePlaceholder &&isUploading ? (
              <div className="flex flex-row gap-4 items-center ">
                <UploadingLoader />
                <span>Uploading File...</span>
              </div>
            ) : isRecording ? (
              "Listening..."
            ) : null}
          </div>
        )}
        {!useNativePlaceholder && !inputText && !inputFocused && (
          <div
            className={classNames(
              "absolute font-inter text-[#1B5C2980] top-1/2 left-5 transform -translate-y-1/2 pointer-events-none text-[14px] leading-[20px] font-normal",
              {
                "!right-28": showInlineUtilities && !showModelSelector,
                "!right-[180px] sm:!right-[200px]": showInlineUtilities && showModelSelector,
                "!right-5": !showInlineUtilities,
              }
            )}
            // style={{ right: "112px" }}
          >
            {isUploading ? (
              <div className="flex flex-row gap-4 items-center ">
                <UploadingLoader />
                <span>Uploading File...</span>
              </div>
            ) : isRecording ? (
              "Listening..."
            ) : placeholder ? (
              placeholder
            ) : (
              "Ask DaburGPT"
            )}
          </div>
        )}
        {showInlineUtilities && (
          <div className="absolute inset-y-0 right-0 flex items-center space-x-3 pr-4">
          {/* Model Selector */}
          {showModelSelector && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={modelName}
                onChange={(e) => onModelChange(e.target.value)}
                sx={{
                  fontSize: "11px",
                  height: "28px",
                  backgroundColor: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1B5C29",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1B5C29",
                  },
                }}
              >
                {Object.keys(modelOptions).map((key) => (
                  <MenuItem key={key} value={key} sx={{ fontSize: "11px" }}>
                    {modelOptions[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {/* File Upload Button */}

          {isFileUploadOptionVisible && (
            <div
              className={classNames(
                "inline-flex h-9 w-9 items-center justify-center gap-2.5 rounded-xl p-1.5",
                {
                  "cursor-pointer":
                    !isUploading &&
                    !chats[chats?.length - 1]?.doc &&
                    files?.length === 0 &&
                    !isContentRendering,
                  "cursor-not-allowed":
                    isUploading ||
                    chats[chats?.length - 1]?.doc ||
                    files?.length > 2 ||
                    isContentRendering ||
                    hasDocument,
                }
              )}
            >
              <div className="flex h-6 w-6 items-center justify-center">
                <div className="relative">
                  <button
                    onClick={() =>
                      !isUploading &&
                      !chats[chats?.length - 1]?.doc &&
                      files?.length < 3 &&
                      !isContentRendering &&
                      fileInputRef.current.click()
                    }
                    disabled={
                      isUploading ||
                      (isMultipleFileUploadEnabled && files?.length >= 3) ||
                      (!isMultipleFileUploadEnabled && files?.length === 1) ||
                      isContentRendering ||
                      hasDocument
                    }
                    className={classNames("flex items-center justify-center", {
                      "cursor-pointer":
                        !isUploading &&
                        !chats[chats?.length - 1]?.doc &&
                        files?.length === 0 &&
                        !isContentRendering,
                      "cursor-not-allowed":
                        isUploading ||
                        chats[chats?.length - 1]?.doc ||
                        (isMultipleFileUploadEnabled && files?.length >= 3) ||
                        (!isMultipleFileUploadEnabled && files?.length === 1) ||
                        isContentRendering ||
                        hasDocument,
                    })}
                  >
                    <UploadIcon
                      color={
                        isUploading ||
                        chats[chats?.length - 1]?.doc ||
                        (isMultipleFileUploadEnabled && files?.length >= 3) ||
                        (!isMultipleFileUploadEnabled && files?.length === 1) ||
                        isContentRendering ||
                        hasDocument
                          ? "#999999"
                          : "rgba(27, 92, 41, 1)"
                      }
                    />
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Microphone Button */}
          <div
            className={classNames({
              "flex h-[45px] w-[45px] place-content-center items-center rounded-full border-opacity-50":
                isRecording,
              "animate-zoom-in-zoom-out ![border-color:#8FD49E]": isRecording,
            })}
            // style={{
            //   borderColor: "rgb(143, 212, 158)",
            // }}
          >
            <button
            type="button"
              className={classNames("bg-[#FFFFFF] flex", {
                "w-full h-full rounded-full content-center !bg-[#1b5c29]":
                  isRecording,
                "cursor-pointer": !isUploading && !isContentRendering,
                "cursor-not-allowed": isUploading || isContentRendering,
                "!opacity-50": isUploading || isContentRendering, // <-- opacity now controlled here!
                "!opacity-100": !isUploading && !isContentRendering,
              })}
              onClick={() => {
                if (!isUploading && !loading && !isContentRendering) {
                  startRecording();
                }
              }}
            >
              <Microphone
                color={
                  isUploading || isContentRendering
                    ? "#999999"
                    : isRecording
                    ? "#FFFFFF"
                    : "rgba(27, 92, 41, 1)"
                }
              />
            </button>
          </div>

          {!loading &&
          !isContentRendering &&
          (
            requireMediaToSend
              ? (inputText?.trim()?.length > 0 && files?.length > 0)
              : ((inputText?.length > 0) || files?.length > 0) && !(files?.length > 0 && !inputText?.trim()?.length)
          ) ? (
            <button
            type="button"
              className={classNames(
                "inline-flex h-9 w-9 items-center justify-center gap-2.5 rounded-xl p-1.5",
                {
                  "bg-secondary cursor-pointer":
                    !isUploading && (
                      requireMediaToSend
                        ? (inputText?.trim()?.length > 0 && files?.length > 0)
                        : ((inputText?.length > 0) || files?.length > 0)
                    ),
                  "bg-gray-200 cursor-not-allowed":
                    isUploading || (
                      requireMediaToSend
                        ? !(inputText?.trim()?.length > 0 && files?.length > 0)
                        : (inputText?.length === 0 && !files?.length) || (files?.length > 0 && !inputText?.trim()?.length)
                    ),
                }
              )}
              onClick={() =>
                !isUploading &&
                !loading &&
                !isContentRendering &&
                (!requireMediaToSend || (files?.length > 0 && inputText?.trim()?.length > 0)) &&
                !(files?.length > 0 && !inputText?.trim()?.length) &&
                handleSend()
              }
            >
              <div className="flex h-6 w-6 items-center justify-center">
                <div className="relative">
                  <SendLogo
                    color={
                      isUploading || isContentRendering || (
                        requireMediaToSend
                          ? !(inputText?.trim()?.length > 0 && files?.length > 0)
                          : (inputText?.length === 0 && !files?.length) || (files?.length > 0 && !inputText?.trim()?.length)
                      )
                        ? "#999999"
                        : "rgba(27, 92, 41, 1)"
                    }
                  />
                </div>
              </div>
            </button>
          ) : (
            isVoiceModeOn &&
            !loading &&
            !isContentRendering && (
              <button
                onClick={handleOpen}
                className="cursor-pointer rounded-xl !bg-[#1b5c29] !shadow-[0_2px_16px_#0002]"
              >
                <span className="flex items-center justify-center w-9 h-9">
                  <VoiceModeIcon color="#ffffff" />
                </span>
              </button>
            )
          )}
          </div>
        )}
      </div>
    </div>
  );
}

TextInput.propTypes = {
  placeholder: PropTypes.string,
  inputFocused: PropTypes.bool,
  inputRef: PropTypes.any,
  setInputFocused: PropTypes.func,
  setInputText: PropTypes.func,
  inputText: PropTypes.string,
  handleChatResponse: PropTypes.func,
  loading: PropTypes.bool,
  fileInputRef: PropTypes.any,
  handleFileChange: PropTypes.func,
  isUploading: PropTypes.bool,
  files: PropTypes.array,
  setFiles: PropTypes.func,
  chats: PropTypes.array,
  handleOpen: PropTypes.func,
  isFileUploadOptionVisible: PropTypes.bool,
  isVoiceModeOn: PropTypes.bool,
  allowedFileTypes: PropTypes.string,
  isContentRendering: PropTypes.bool,
  hasDocument: PropTypes.bool,
  isMultipleFileUploadEnabled: PropTypes.bool,
  inputVariant: PropTypes.oneOf(["boxed", "flat"]),
  showInlineUtilities: PropTypes.bool,
  useNativePlaceholder: PropTypes.bool,
  showPromptActions: PropTypes.bool,
  showModelSelector: PropTypes.bool,
  modelOptions: PropTypes.object,
  modelName: PropTypes.string,
  onModelChange: PropTypes.func,
};
